import React from 'react';
import { ETATS_RESEAU, MODES_CHAUFFAGE } from '../../utils/reseaux';

// Logements & réseaux — le panneau de l'étape Travaux qui pilote les lots
// techniques : un logement par ligne (nom, niveaux, surface habitable, état de
// chaque réseau), un mode de chauffage, et deux actions — proposer d'après les
// plans, générer les lignes. Le moteur est src/utils/reseaux.js ; les prix,
// ceux de la grille. Rien ici n'est calculé : la page fournit tout.

const fmt = (n) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n || 0);

export default function LogementsReseaux({ logements, onChange, modeChauffage, onModeChauffage, onProposer, onGenerer, resume, doublons, onRetirerDoublons, ouvert, onToggle }) {
  const maj = (i, patch) => onChange(logements.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const retirer = (i) => onChange(logements.filter((_, j) => j !== i));
  const ajouter = () => onChange([...logements, { id: `log-${Date.now()}`, nom: `Logement ${logements.length + 1}`, niveaux: [], surface: 0, electricite: 'a_refaire', plomberie: 'a_refaire', chauffage: 'a_refaire' }]);
  const surfaceTotale = logements.reduce((s, l) => s + (Number(l.surface) || 0), 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100">
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
        <div className="min-w-0">
          <h3 className="font-bold text-slate-900 text-sm">Logements &amp; réseaux</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {logements.length
              ? `${logements.length} logement${logements.length > 1 ? 's' : ''} · ${fmt(surfaceTotale)} m² habitables${resume?.lignes ? ` · ${resume.lignes} lignes réseaux, ${fmt(resume.total)} € HT` : ' · lignes réseaux non générées'}`
              : 'Électricité, plomberie et chauffage se chiffrent par logement, pas au point ni au mètre.'}
          </p>
        </div>
        <span className="text-slate-400 text-xs shrink-0">{ouvert ? 'Replier' : 'Ouvrir'}</span>
      </button>

      {ouvert && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          {logements.length === 0 ? (
            <p className="text-sm text-slate-600">Aucun logement défini. Proposez-les d'après les plans (un par niveau habitable, garage et cave exclus), puis corrigez : fusionner un duplex, scinder un plateau, ajuster une surface.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
                    <th className="text-left py-1 pr-2">Logement</th>
                    <th className="text-left py-1 pr-2">Niveaux</th>
                    <th className="text-right py-1 pr-2">m² hab.</th>
                    <th className="text-left py-1 pr-2">Électricité</th>
                    <th className="text-left py-1 pr-2">Plomberie</th>
                    <th className="text-left py-1 pr-2">Chauffage</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {logements.map((l, i) => (
                    <tr key={l.id || i} className="border-t border-slate-100">
                      <td className="py-1.5 pr-2">
                        <input value={l.nom} onChange={e => maj(i, { nom: e.target.value })} className="champ w-36" aria-label="Nom du logement" />
                      </td>
                      <td className="py-1.5 pr-2 text-slate-500 whitespace-nowrap">{(l.niveaux || []).join(' + ') || '—'}</td>
                      <td className="py-1.5 pr-2 text-right">
                        <input type="number" step="0.5" min="0" value={l.surface} onChange={e => maj(i, { surface: parseFloat(e.target.value) || 0 })} className="champ w-20 text-right tabular-nums" aria-label="Surface habitable" />
                      </td>
                      {['electricite', 'plomberie', 'chauffage'].map(r => (
                        <td key={r} className="py-1.5 pr-2">
                          <select value={l[r]} onChange={e => maj(i, { [r]: e.target.value })} className="champ select-compact pr-7"
                                  aria-label={`État ${r}`}>
                            {ETATS_RESEAU.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
                          </select>
                        </td>
                      ))}
                      <td className="py-1.5 text-right">
                        <button type="button" onClick={() => retirer(i)} className="text-[11px] text-slate-400 hover:text-red-600">retirer</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={onProposer} className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50">
              {logements.length ? 'Reproposer d’après les plans' : 'Proposer d’après les plans'}
            </button>
            <button type="button" onClick={ajouter} className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50">+ Logement</button>
            <label className="flex items-center gap-2 text-xs text-slate-600 ml-auto">Chauffage
              <select value={modeChauffage} onChange={e => onModeChauffage(e.target.value)} className="champ select-compact pr-7">
                {MODES_CHAUFFAGE.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button type="button" onClick={onGenerer} disabled={!logements.length}
                    className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed">
              {resume?.lignes ? 'Régénérer les lignes réseaux' : 'Générer les lignes réseaux'}
            </button>
            <p className="text-xs text-slate-500 min-w-0">
              Les lignes vont dans une pièce « Installations techniques » par logement et une pièce « Parties communes &amp; bâtiment » ; les corriger à la main reste possible, régénérer les remplace.
            </p>
          </div>

          {doublons > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex flex-wrap items-center gap-2">
              <span>{doublons} ligne{doublons > 1 ? 's' : ''} à l’unité (points, prises, radiateurs, réseaux de salle de bain…) dans les pièces feraient double emploi avec les forfaits par logement.</span>
              <button type="button" onClick={onRetirerDoublons} className="font-semibold underline hover:text-amber-700">Retirer ces lignes des pièces</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
